import { isNil, mapValues, omitBy } from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * NOTE: React Router v6 has useSearchParams, which works somewhat like this hook, though
 * is not well typed. Consider using it, or possibly refactoring this hook to use it.
 *
 * A custom query param handler that works nicely with React Router and Typescript interfaces.
 * Takes a parameter of keys to control: only those are read and set, others are left alone.
 * Allows using url as global key:string value store.
 *
 * OA: When reading query parameters, converts array param to a JS array
 * if the param is given in the following format:
 *
 * key=item1&key=item2&key=item3
 *
 * The format kinda sucks, and would probably be better to just use custom strings to represent
 * arrays, but the above syntax seems to be used widely in the JS world (though there's no standard)
 * So I decided to use it. The URLSearchParams.getAll() method parses array params in this format automatically.
 *
 * ML: Note convention, undefined or null is set as no query param, and empty query param is
 * read as undefined. If you want to use null as query param, change is needed here.
 */

export function useQueryParams<QueryParams extends { [K in keyof QueryParams]?: string | string[] }>(
  params: Array<keyof QueryParams>,
): [Partial<QueryParams>, (paramsToSet: Partial<QueryParams>, method?: 'replace' | 'push') => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const queryParams: Partial<QueryParams> = {};
  params.forEach(param => {
    const paramValue = searchParams.getAll(param as string) as string[] | undefined;
    const valueToUse = (() => {
      if (!paramValue || !paramValue.length) {
        return undefined;
      }
      return paramValue.length === 1 ? parseParam(paramValue[0]) : paramValue.map(parseParam);
    })();
    queryParams[param] = (valueToUse as QueryParams[keyof QueryParams]) || undefined;
  });

  const setQueryParams = (paramsToSet: Partial<QueryParams>, method?: 'replace' | 'push') => {
    // ML :DOES NOT SUPPORT WRITING ARRAYS TO URL.
    // Destructuring URLSearchParams into object seems necessary in order to not ovewrite
    // parameters not handled by the hook.
    const oldParams = Object.fromEntries(searchParams);
    const newParams = { ...oldParams, ...paramsToSet };
    // Instead of setting to undefined/null, delete from URL.
    const newParamsNoNils = omitBy(newParams, isNil);

    // Use URLSearchParams object to produce the string
    const newParamsObject = new URLSearchParams(newParamsNoNils);

    const loc = { pathname: location.pathname, search: newParamsObject.toString() };

    switch (method) {
      case 'push':
        navigate(loc);
        break;
      case 'replace':
      default:
        navigate(loc, { replace: true });
    }
  };

  return [queryParams, setQueryParams];
}

function parseParam(param: string) {
  const decoded = decodeURIComponent(param);
  // OA: When running decodeURIComponent for stuff that has HTML, some special characters seem to result in unexpected
  // line breaks that later on break JSON parsing, so we'll strip them already here.
  const stripped = decoded.replace(/(\r\n|\n|\r)/gm, '');
  return stripped;
}

// Can't create array params, tread lightly.
export function joinQueryParams<T extends object>(queryParams: T | undefined) {
  if (!queryParams) {
    return '';
  }

  // OA 1/22: I added some ugly casting stuff to keep the linter happy.
  // A proper fix would be to stringify the object values or do some other validation, but I don't have time to go there now.
  const joinedParams = Object.values(
    mapValues(queryParams, (value, key) => `${key}=${value as unknown as string}`),
  ).join('&');
  return `?${joinedParams}`;
}
